import React from "react";

import { IUser, UserDTO } from "./user.type";

// Re-export examination types
export type {
  QuestionAction,
  QuestionSelection,
  ModuleSelection,
  UnitSelection,
  BloomsDistribution,
  QuestionExportFilters,
  QuestionActionExportFilters,
  ExamScheduleFilters,
  // Education Programs (replaces Question Categories)
  EducationProgram,
  CreateEducationProgramRequest,
  UpdateEducationProgramRequest,
  EducationProgramFilters,
  // Legacy Question Categories (kept for backward compatibility)
  QuestionCategory,
  CreateQuestionCategoryRequest,
  UpdateQuestionCategoryRequest,
  QuestionCategoryFilters,
  // Question Modules
  QuestionModule,
  CreateQuestionModuleRequest,
  UpdateQuestionModuleRequest,
  // Question Units
  QuestionUnit,
  CreateQuestionUnitRequest,
  UpdateQuestionUnitRequest,
  // Bloom's Taxonomy Levels
  BloomsTaxonomyLevel,
  CreateBloomsTaxonomyLevelRequest,
  UpdateBloomsTaxonomyLevelRequest,
  // Exam Schedules
  ExamSchedule,
  CreateExamScheduleRequest,
  ExamSchedulePublicDTO,
  // Exam Sessions
  ExamSession,
  ExamSessionDTO,
  ActiveExamScheduleDTO,
  // Exam Results
  ExamResultDTO,
  // Exam Appeals
  ExamAppealDTO,
  SubmitAppealRequest,
  ReviewAppealRequest,
  AppealEligibilityResponse,
  // Exam Retakes
  UserExaminationAttemptDTO,
  PaymentProofDTO,
  InitiateRetakeRequest,
  PaymentProofUploadRequest,
  RetakeEligibilityResponse,
  // Exam Session Actions
  ExamSessionAction,
  EndSessionRequest,
  BlockSessionRequest,
  SubmitAllAnswersWithReasonRequest,
  // Answer Submission
  SubmitAnswerRequest,
  SubmitAllAnswersRequest,
  SubmittedAnswerDTO,
  // Verification
  VerifyExamSessionRequest,
  VerifyExamSessionResponse,
  // Time Extension
  ExtendTimeRequest,
  ExtendTimeResponse,
} from "./examination.types";

export {
  AppealType,
  EAppealStatus,
  EOutcomeImpact,
  EAttemptStatus,
  EApprovalStatus,
} from "./examination.types";

// Irembo types
export type {
  IremboPaymentConfig,
  CreateIremboConfigRequest,
  VerifyIremboConfigRequest,
  IremboConfigResponse,
  Council as IremboCouncil,
} from "./irembo.types";

export type {
  Payment,
  PaymentMethod,
  PaymentStatus,
  InitiatePaymentRequest,
  InitiatePaymentResponse,
  PaymentStatusResponse,
  UserPaymentsResponse,
  PaymentValidationResponse,
  UpdatePaymentStatusRequest,
  UpdatePaymentStatusResponse,
  IremboPayCustomer,
  IremboPayInvoiceRequest,
  IremboPayInvoiceResponse,
  CouncilPayment,
  CouncilPaymentsResponse,
  CouncilPaymentsFilters,
} from "./payment.types";

export interface IModel {
  id: string;
  lastUpdatedOn: string;
  createdOn: string;
}

export enum UserRole {
  ADMIN = "ADMIN",
  ENDUSER = "ENDUSER",
  VERIFIER = "VERIFIER",
  FIRSTREVIEWER = "FIRSTREVIEWER",
  SECONDREVIEWER = "SECONDREVIEWER",
  APPROVER = "APPROVER",
  ADMINCOUNCIL = "ADMINCOUNCIL",
  INVIGILATOR = "INVIGILATOR",
  EXAMINER = "EXAMINER",
}

export enum Visibility {
  VISIBLE = "VISIBLE",
  HIDDEN = "HIDDEN",
}

export interface IStatistic {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: JSX.Element;
  /**vi
   * 6-digit HEX code of the color
   */
  color: string;
}

export interface SideBarRouteOption {
  path: string;
  icon: React.ComponentType<any>;
  exact?: boolean;
  routes?: SideBarRouteOption[];
  name: string;
  altPaths?: string[];
  iconSize?: number;
  roleAcessible?: string[];
  hasSubRoutes?: boolean;
}

export interface ProfileProps {
  profileUrl?: string;
  email: string;
  companyName: string;
}

export interface TabFiltersArgs {
  data: any[];
  sortedData: any[];
  setData?: React.Dispatch<React.SetStateAction<any[]>>;
  reset?: () => void;
}

export interface ISidebarProps {
  isActive: boolean;
}

export interface SideBarRoute {
  manage: SideBarRouteOption[];
  options: SideBarRouteOption[];
}

export interface ICertificateapplication {
  certificateAmount: number;
  depositDate: Date | null;
  admissionDate: Date | null;
  professionCategoryApplied: string;
  awardApplied: string;
  transactionNumber: string;
  bankId: string;
  alreadyRegistred: "Yes" | "No";
  nationalityCheck: "Yes" | "No" | "";
  remarks: string;
  previousCertificateDetails: string;
  previousCertificateFile: File | null;
  uploadCriminalRecordPath: File | null;
  uploadMedicalRecordPath: File | null;
  uploadReceipt: File | null;
  universityAdmissionLetter: File | null;
  uploadOtherDocumentPath: File | null;
  equivalence: File | null;
  passport: File | null;
  validVisa: File | null;
  residencePermit: File | null;
  transcript: File | null;
  cpdProof: File | null;
  proofOfRegistration: File | null;
  appointmentLetter?: File | null;
  serviceId: string;
  paymentMethod?: string;
  
  // 🆕 New Education Program fields for EXAMINATION service
  diplomasFile?: File | null;
  educationInstitutionId?: string;
  educationProgramId?: string;
  
  // 🆕 Year for calculation field for LICENSE RENEWAL service (replaces yearOfLicence)
  yearForCalculation?: number;
  // 🆕 Area of practice field (renamed from previousInstitution)
  areaOfPractice?: string;
  // 🆕 Optional explicit licence dates (used if yearForCalculation is not provided)
  licenceDate?: string;
  licenceExpiredDate?: string;
  
  // 🆕 Good Standing Certificate for NPC INTERNSHIP foreigners
  goodStandingCertificate?: File | null;

  // RMDC council only — optional supplementary documents
  internshipCertificate?: File | null;
  recommendationLetter?: File | null;
  registrationCertificate?: File | null;
}

// For user registration fees (Array of certificates with a name and amount)
export interface RegistrationFee {
  name: string;
  amount: number;
}

// For user bank details
export interface Bank {
  id: string;
  name: string;
  accountNo: string;
  branchName: string;
}

// For profession category information
export interface ProfessionCategory {
  id: string;
  name: string;
  code: string;
  examRequired: boolean;
  isRequiredinLowLevel: boolean;
}

// For certificate application
export interface CertificateApplication {
  certificateAmount: number;
  depositDate: Date;
  transactionNumber: string;
  bankId: string;
  alreadyRegistred: string;
  remarks: string;
  previousCertificateDetails: string;
  previousCertificateFile: File | null;
  uploadReceipt: File | null;
  professionCategoryApplied: string;
  awardApplied: string;
  serviceId: string;
}

// For fetched application
export interface UserApplication {
  id: string;
  certificateAmount: number;
  transactionNumber: string;
  alreadyRegistered: string;
  status: string;
  remarks: string;
  professionCategoryApplied: string;
  awardApplied: string;
  serviceId: string;
  statusLevel: string;
  actionDoneBy: string;
  exitAward: string;
  // Add other properties as needed
}

export interface UserFines {
  id: string;
  applicationId: string;
  finesDate: Date;
  approvalDate: Date;
  userId: string;
  comment: string;
  decision: string;
  approvalLevel: number;
  userPosition: string;
  certificateFinesAmount: number;
  applicationName: string;
  applicantId: string;
  // Add other properties as needed
}

export interface OnholdApplications {
  applicationId: string;
  userId: string;
  comment: string;
  applicationName: string;
  applicantId: string;
  uploadFilePath: File | null;
  transDate: Date;
  // Add other properties as needed
}

export interface ApprovalOnholdApplication {
  id: string;
  applicationId: string;
  userId: string;
  comment: string;
  applicationName: string;
  applicantId: string;
  uploadReceiptFilePath: File | null;
  transactionDate: Date;
  userPosition: string;
  // Add other properties as needed
}

export interface UserFinesPayment {
  amountPayed: number;
  applicationId: string;
  comment: string;
  depositDate: Date | null;
  fineId: string;
  transactionDate: Date | null;
  uploadReceiptFilePath: File | null;
  userId: string;
  transactionNumber: string;
  bankId: string;
  applicationName: string;

  // Add other properties as needed
}

// For the user's approved certificates
export interface UserCertificateApproved {
  id: string;
  /** CertificateApplication.id returned by GET /applicationconfirmed/user/:userId. Use this for verify-license (not id, which is ApplicationConfirmed.id). */
  applicationId?: string;
  /** @deprecated Prefer applicationId from API. When present, use for verify-license. */
  certificateApplicationId?: string;
  /** When present with indexNumber, use getCertificateVerificationUrlByIndexAndService as fallback. */
  serviceId?: string;
  certificateName: string;
  status: string;
  applicationName: string;
  indexNumber: string;
  date: string; // Or Date if you are using Date objects
  expiredDate: string;
  licenceDate?: string;
  licenceExpiredDate?: string;
  userDetails: UserVerf;
  levelOfStudy: string;
  /** Server-derived: true iff licenceExpiredDate is non-null and in the past. Do not submit on write. */
  isExpired?: boolean;
}

export interface Role {
  roleId: string;
  roleName: string;
}

export interface Encounter {
  id: string;
  createdOn: string;
  lastUpdatedOn: string;
  createdBy: IUser;
  lastUpdatedBy: IUser | null;
  visitDate: string;
}

export interface ApprovalRequest {
  createdOn: string;
  comment?: string;
  currentApprovalStatus: "PENDING" | "APPROVED" | "REJECTED";
  id: string;
  approvalDate?: string;
}

export interface UserVerf {
  firstName: string;
  lastName: string;
  nationalID: string;
}

export interface Verification {
  userPosition: string;
  user: UserVerf;
  approvalDate: string;
  decision: string;
  comment: string;
}

export enum EServiceStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
}

export interface ApplicationConfirmedStatusRequest {
  status: "ACTIVE" | "INACTIVE";
  reason?: string;
}

export interface ApplicationConfirmedStatusResponse {
  message: string;
  status: string;
  data: {
    id: string;
    status: string;
    [key: string]: any;
  };
}

export type DependencyOperator = "AND" | "OR";

export interface ServiceDependencyGroup {
  operator: DependencyOperator;
  dependentServiceIds: string[];
  dependentServices?: Service[]; // Populated in responses, ignored in requests
}

export interface Service {
  id: string;
  name: string;
  council_id: string;
  registrationFee: number;
  code: string;
  productCode?: string | null;
  approvalFinalLevel: number;
  dependentServices: Service[]; // Legacy field for backward compatibility
  dependentServiceIds?: string[]; // Legacy field for requests (backward compatibility)
  dependencyGroups?: ServiceDependencyGroup[]; // New structure for complex dependencies
  status: EServiceStatus;
  renewable?: boolean; // Optional field for backward compatibility - defaults to false if not provided
  baseServiceId?: string | null; // ID of base/original service this renewal belongs to; null for non-renewals
  tblcouncil?: {
    id: string;
    councilName: string;
    councilCode: string;
  };
}

export interface Council {
  id: string;
  councilName: string;
  councilCode: string;
  councilProfileDocumentPath: string;
  address: string;
  telephone: string;
  emails: string[];
  website: string;
  registrarName?: string | null;
}

export enum ECouncilName {
  NPC = "NPC",
  RMDC = "RMDC",
  NCNM = "NCNM",
  RAHPC = "RAHPC",
}


export enum EUserRoleName {
  ADMIN = "ADMIN",
  ADMINCOUNCIL = "ADMINCOUNCIL",
}

export interface StudyLevelConfiguration {
  id: string;
  studyLevelName: string;
  council: Council;
  service: Service;
}

export interface CreateStudyLevelConfigurationRequest {
  studyLevelName: string;
  councilId: string;
  serviceId: string;
}

export interface UpdateStudyLevelConfigurationRequest extends CreateStudyLevelConfigurationRequest {
  id: string;
}

// Legacy Fine interfaces (kept for backward compatibility)
export interface Fine {
  id: string;
  baseRatio: number;
  safePeriodDays: number;
  increaseUntilDay: number;
  council: Council;
  service: Service;
  productCode?: string;
  description?: string;
}

export interface CreateFineRequest {
  baseRatio: number;
  safePeriodDays: number;
  increaseUntilDay: number;
  councilId: string;
  serviceId: string;
  productCode?: string;
  description?: string;
}

export interface UpdateFineRequest extends CreateFineRequest {
  id: string;
}

// Enhanced Fine Management (based on API contract)
export interface FineRule {
  id: string;
  name: string;
  description: string;
  productCode: string;
  amount: number;
  councilId: string;
  serviceId: string;
  isActive: boolean;
  effectiveDate: string;
  rules: {
    daysAfterDeadline?: number;
    calculateDailyIncrease?: boolean;
    dailyIncreaseAmount?: number;
    maxAmount?: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface CreateFineRuleRequest {
  name: string;
  description: string;
  productCode: string;
  amount: number;
  councilId: string;
  serviceId: string;
  isActive: boolean;
  effectiveDate: string;
  rules: {
    daysAfterDeadline?: number;
    calculateDailyIncrease?: boolean;
    dailyIncreaseAmount?: number;
    maxAmount?: number;
  };
}

export interface UpdateFineRuleRequest extends CreateFineRuleRequest {
  id: string;
}

// Application Fine Details
export interface ApplicationFineDetail {
  amount: number;
  calculatedDate: string;
  dueDate: string;
  productCode: string;
  status: 'PENDING' | 'PAID' | 'OVERDUE';
  paidDate?: string;
  reasons: string[];
  description: string;
  daysPastDue: number;
}

export interface ApplicationFineCalculation {
  applicationId: string;
  applicationName: string;
  applicantId: string;
  applicantName: string;
  councilId: string;
  councilName: string;
  fines: ApplicationFineDetail[];
  totalFineAmount: number;
  totalFinesCount: number;
  pendingFinesCount: number;
  paidFinesCount: number;
  hasPendingFines: boolean;
}

// New API types for user applications payment status
export interface FineDetail {
  amount: number;
  calculatedDate: string;
  dueDate: string;
  productCode: string;
  status: string;
  paidDate: string | null;
  reasons: string[];
  description: string;
  daysPastDue: number | null;
  // Legacy fields for backward compatibility
  fineType?: string;
  appliedDate?: string;
}

export interface ApplicationFines {
  hasPendingFines: boolean;
  totalFineAmount: number;
  fineDetails: FineDetail[];
}

export interface ApplicationPayment {
  id: string;
  transactionId: string;
  invoiceNumber: string | null;
  paymentMethod: string;
  paymentStatus: string;
  amount: number;
  currency: string;
  paymentLinkUrl: string | null;
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
  manualTransactionNumber: string | null;
  receiptFilePath: string | null;
  bankId: string | null;
  publicKey: string | null;
  user: any | null;
  service: any | null;
  serviceName: string | null;
  council: any | null;
  bank: any | null;
  // Legacy fields for backward compatibility
  paymentId?: string;
}

export interface UserApplicationPaymentStatus {
  applicationId: string;
  serviceId: string;
  serviceName: string;
  applicationStatus: string;
  paymentRequired: boolean;
  paymentStatus: string | null;
  rejectionReasons: string[];
  payment: ApplicationPayment | null;
  fines: ApplicationFines;
  totalAmountDue: number;
  canProceedWithPayment: boolean;
  nextActions: string[];
}

export interface InitiateFinePaymentRequest {
  applicationId: string;
  paymentMethod: string;
  transactionNumber?: string;
  depositDate?: string;
  bankId?: string;
  uploadReceipt?: File;
}

// Registration Number Assignment Types
// Deprecated: Use AssignOrUpdateRegistrationNumberRequest instead
export interface AssignRegistrationNumberRequest {
  userId: string;
  councilId: string;
}

// New unified request type for assign-or-update endpoint
export interface AssignOrUpdateRegistrationNumberRequest {
  userId: string;
  councilId: string;
  registrationNumber?: string | null; // Optional: if null, auto-generates
  previouslyLicensed?: boolean; // Optional: default false
  licenseDate?: string; // Required if previouslyLicensed=true (ISO-8601 date)
  licenseExpiredDate?: string; // Required if previouslyLicensed=true (ISO-8601 date)
}

export interface RegistrationNumberResponse {
  id: number;
  userId: string;
  councilId: string;
  registrationNumber: number;
  formattedRegistrationNumber?: string; // Formatted per council (e.g., "NPC/A6101")
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    username: string;
    [key: string]: any;
  };
  tblCouncil: {
    id: string;
    name: string;
    councilName: string;
    councilCode: string;
    [key: string]: any;
  };
  createdAt: string;
  updatedAt: string;
  deleted: boolean;
}

// Old Confirmed Application type (for previously licensed users)
export interface OldConfirmedApplication {
  id: string;
  indexNumber: string; // Full registration number string
  date: string; // ISO-8601 date
  userId: string;
  applicationName: string;
  status: string;
  expiredDate: string; // ISO-8601 date
  userDetails?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    username: string;
    [key: string]: any;
  };
}

// Response type for assign-or-update endpoint
export interface AssignOrUpdateRegistrationNumberResponse {
  registrationNumber: RegistrationNumberResponse;
  oldConfirmedApplication: OldConfirmedApplication | null;
  wasUpdated: boolean;
  message: string;
}

// Index Assignment Types
export interface AssignOrUpdateIndexRequest {
  userId: string;
  councilId: string;
  indexNumber?: string | null;
  previouslyIndexed?: boolean;
  indexDate?: string;
  indexExpiredDate?: string;
}

export interface IndexResponse {
  id: number;
  userId: string;
  councilId: string;
  indexNumber: number;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    username: string;
    [key: string]: any;
  };
  tblCouncil: {
    id: string;
    name: string;
    councilName: string;
    councilCode: string;
    [key: string]: any;
  };
  createdAt: string;
  updatedAt: string;
  deleted: boolean;
}

export interface AssignOrUpdateIndexResponse {
  indexNumber: IndexResponse;
  oldConfirmedApplication: OldConfirmedApplication | null;
  wasUpdated: boolean;
  message: string;
}

// Shared DTOs for complete index/registration endpoints
type RegistrationUserDTO = RegistrationNumberResponse["user"];
type RegistrationCouncilDTO = RegistrationNumberResponse["tblCouncil"];
type IndexUserDTO = IndexResponse["user"];
type IndexCouncilDTO = IndexResponse["tblCouncil"];

// Historical application record (INDEXING or LICENSE)
export interface ApplicationOldConfirmedDTO {
  id: string;
  indexNumber: string;
  date: string;
  userId: string;
  applicationName: string;
  status: string;
  expiredDate: string;
  userDetails: RegistrationUserDTO;
}

// Complete index response wrapper
export interface UserIndexDTO {
  id: number;
  indexNumber: number;
  formattedIndexNumber: string;
  userId: string;
  councilId: string;
  user: IndexUserDTO;
  tblCouncil: IndexCouncilDTO;
  createdAt: string;
  updatedAt: string;
  deleted: boolean;
}

export interface IndexCompleteResponseDTO {
  user: IUser;
  indexNumber: UserIndexDTO | null;
  oldApplications: ApplicationOldConfirmedDTO[];
  message: string;
}

// Complete registration response wrapper
export interface UserRegistrationNumberDTO {
  id: number;
  registrationNumber: number;
  formattedRegistrationNumber: string;
  userId: string;
  councilId: string;
  user: RegistrationUserDTO;
  tblCouncil: RegistrationCouncilDTO;
  createdAt: string;
  updatedAt: string;
  deleted: boolean;
}

export interface RegistrationCompleteResponseDTO {
  user: IUser;
  registrationNumber: UserRegistrationNumberDTO | null;
  oldApplications: ApplicationOldConfirmedDTO[];
  message: string;
}

// System KPI Analytics Types
export type {
  SystemOverview,
  InstitutionPerformance,
  GenderPerformance,
  EducationProgramPerformance,
  AttemptTypePerformance,
  CouncilPerformance,
  YearlyTrends,
  ScoreDistribution,
  AggregateStatistics,
  SystemKPIAnalyticsResponse,
  SystemKPIAnalyticsFilters,
  SystemKPIAnalyticsApiResponse,
  ExportFormat,
  SystemKPIAnalyticsExportRequest,
  SystemKPIAnalyticsErrorResponse,
  ChartDataPoint,
  TimeSeriesDataPoint,
  ComparisonDataPoint,
  FilterOptions,
  KPIDashboardSummary,
  ExportConfiguration,
} from "./system-kpi-analytics.types";